import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { MatCard } from '@angular/material/card';
import { Building } from '@app/shared/models/building.interface';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatChipListbox, MatChipOption, MatChipRemove } from '@angular/material/chips';
import { MatDivider } from '@angular/material/divider';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { SharedComponentsModule } from '@app/shared/shared-components.module';
import { admStaticFilterConfigs, AdmStaticFilterType } from '@app/driver-metrics/static/adm-static-filter/filter.config';
import { MatButton } from '@angular/material/button';
import { MatIcon } from '@angular/material/icon';
import { DriverStaticFilterCriteria, DriverStaticFilterService } from '@app/driver-metrics/static/driver-static-filter.service';
import { TagService } from '@services/tag.service';
import { takeUntil } from 'rxjs/operators';
import { Floor } from '@app/shared/models/floor.interface';
import { Tag } from '@app/shared/models/tag.interface';
import { Subject } from 'rxjs';

@Component({
  selector: 'app-adm-static-filter',
  imports: [
    MatCard,
    MatButton,
    MatCheckbox,
    MatChipListbox,
    MatChipOption,
    MatChipRemove,
    MatDivider,
    MatIcon,
    ReactiveFormsModule,
    SharedComponentsModule
  ],
  templateUrl: './adm-static-filter.component.html',
  styleUrl: './adm-static-filter.component.scss'
})
export class AdmStaticFilterComponent implements OnInit, OnDestroy {
  protected readonly admStaticFilterConfigs = admStaticFilterConfigs;
  private readonly destroy$ = new Subject<void>();

  @Input()
  building: Building;

  form: FormGroup;
  selectedOption: AdmStaticFilterType;
  isFilterSelected = false;
  isFilterSelectionChanged = false;
  isFilterApplied = false;
  previousFilterState = { floor: [], tag: [] };

  constructor(
    private readonly filterService: DriverStaticFilterService,
    private readonly fb: FormBuilder,
    private readonly tagService: TagService
  ) {}

  ngOnInit(): void {
    this.tagService
      .getTagsForBuildingId(this.building.id, true)
      .pipe(takeUntil(this.destroy$))
      .subscribe((tags) => {
        this.form = this.fb.group({
          floor: this.fb.array(this.building.floors.map((floor) => this.getInnerFormGroup(floor))),
          tag: this.fb.array(tags.map((tag) => this.getInnerFormGroup(tag)))
        });
        if (!this.filterService.isEmptyFilterCriteria()) {
          this.setFilterValues(this.filterService.filterCriteriaValue);
          this.previousFilterState = this.form.value;
          this.isFilterApplied = true;
        }
        this.filterFormValueChangeListener();
        this.selectItem(admStaticFilterConfigs[0].type);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getInnerFormGroup(property: Floor | Tag): FormGroup {
    return this.fb.group({ id: property.id, name: property.name, checked: false });
  }

  filterFormValueChangeListener(): void {
    this.form?.valueChanges.subscribe((newFormValue) => {
      this.isFilterSelected = Object.keys(newFormValue).some((key) =>
        newFormValue[key].some(({ checked }) => checked)
      );

      this.isFilterSelectionChanged = Object.keys(newFormValue).some((key) =>
        newFormValue[key].some(
          (obj) => obj.checked !== this.previousFilterState[key].find((p) => p.id === obj.id)?.checked
        )
      );
    });
  }

  selectItem(option: AdmStaticFilterType): void {
    this.selectedOption = option;
  }

  formArray(controlName: string): FormArray {
    return this.form.get(controlName) as FormArray;
  }

  filter(): void {
    this.filterService.filterCriteria = {
      floorIds: this.getIds(AdmStaticFilterType.FLOOR.toString()),
      tagIds: this.getIds(AdmStaticFilterType.TAG.toString()),
      buildingId: this.building.id
    };
    this.previousFilterState = this.form.value;
    this.isFilterApplied = true;
    this.isFilterSelectionChanged = false;
  }

  resetFilters(): void {
    this.previousFilterState = { floor: [], tag: [] };
    admStaticFilterConfigs.forEach((config) => {
      this.formArray(config.formKey).controls.forEach((control) => {
        control.get('checked').setValue(false);
      });
    });
    if (this.isFilterApplied) {
      this.filterService.resetFilterCriteria();
    }
    this.isFilterApplied = false;
    this.isFilterSelected = false;
    this.isFilterSelectionChanged = false;
  }

  setFilterValues(filterCriteria: DriverStaticFilterCriteria): void {
    this.formArray(AdmStaticFilterType.FLOOR.toString()).controls.forEach((control) => {
      control.get('checked').setValue(filterCriteria.floorIds.includes(control.get('id').value));
    });
    this.formArray(AdmStaticFilterType.TAG.toString()).controls.forEach((control) => {
      control.get('checked').setValue(filterCriteria.tagIds.includes(control.get('id').value));
    });
  }

  removeSelection(formArray: FormArray, option: any): void {
    formArray.controls
      .filter((control) => control.get('id').value === option.id)
      .forEach((control) => control.get('checked').setValue(false));
  }

  getIds(controlName: string): number[] {
    return this.selectedItemArray(controlName).map((s) => s.id);
  }

  selectedItemArray(controlName: string): any[] {
    return controlName ? this.formArray(controlName)?.value?.filter((s) => s.checked) : [];
  }
}
